import React, { useState, useEffect } from 'react';
import ReactPlayer from 'react-player';
import { useMedia } from './context/MediaContext';
import { useScoreboard } from './context/ScoreboardContext';
import { useMusic } from './context/MusicContext';

const PreGameOverlay = ({ onClose, openVideoPlayer, isVideoPlaying, currentVideoIndex }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);
  const [previewingIndex, setPreviewingIndex] = useState(null);
  const { youtubeVideos } = useMedia();
  const { isEntertainmentEnabled } = useScoreboard();

  const handleVideoSelect = (index) => {
    if (isVideoPlaying && currentVideoIndex === index) {
      // If this video is currently playing, send stop command
      if (window.videoPlayerWindow) {
        window.videoPlayerWindow.postMessage({ type: 'STOP_VIDEO' }, '*');
      }
    } else if (isVideoPlaying) {
      // Another video is already playing, prevent starting a new one
      return;
    } else {
      openVideoPlayer(index);
    }
  };

  const handleStopAndCloseVideoPlayer = () => {
    if (window.videoPlayerWindow && !window.videoPlayerWindow.closed) {
      // Send stop command first
      window.videoPlayerWindow.postMessage({ type: 'STOP_VIDEO' }, '*');
      // Then close the window
      window.videoPlayerWindow.close();
      // Clear the reference
      setTimeout(() => {
        window.videoPlayerWindow = null;
      }, 500);
    }
  };

  // Check if video player window is open
  const isVideoPlayerOpen = window.videoPlayerWindow && !window.videoPlayerWindow.closed;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Pre-Game Entertainment</h1>
          <div className="flex items-center gap-3">
            {isVideoPlayerOpen && (
              <button
                onClick={handleStopAndCloseVideoPlayer}
                className="bg-amber-500 text-black px-4 py-2 rounded-lg hover:bg-amber-600 font-bold text-base"
                title="Stop and close video player"
              >
                ⏹️ Stop & Close Video
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="space-y-6">

          {/* Videos Section */}
          <h2 className="text-lg font-bold mb-4">Videos</h2>
          {!isEntertainmentEnabled ? (
            <div className="text-center text-gray-500 py-8">
              Pre-game video player requires a valid license.
              Please activate your license in the settings.
            </div>
          ) : youtubeVideos.some(video => video.url) ? (
            <div className={`grid ${youtubeVideos.filter(v => v?.url).length > 2 ? 'grid-cols-2 gap-4' : 'grid-cols-1 gap-2'}`}>
              {youtubeVideos.map((video, index) => (
                video.url && (
                  <div
                    key={index}
                    className="flex space-x-4 p-3 rounded-lg hover:bg-gray-50 group relative bg-white"
                  >
                    <button
                      onClick={() => handleVideoSelect(index)}
                      className={`flex-shrink-0 w-12 h-12 text-white rounded-full flex items-center justify-center transition-colors duration-200 mt-1 ${
                        isVideoPlaying && currentVideoIndex === index 
                          ? 'bg-blue-500 hover:bg-blue-600' 
                          : isVideoPlaying 
                            ? 'bg-gray-400 cursor-not-allowed' 
                            : 'bg-amber-500 hover:bg-amber-600'
                      }`}
                      title={
                        isVideoPlaying && currentVideoIndex === index 
                          ? "Stop video" 
                          : isVideoPlaying 
                            ? "Another video is playing" 
                            : "Play video"
                      }
                      disabled={isVideoPlaying && currentVideoIndex !== index}
                      data-testid={`video-${index}-${isVideoPlaying && currentVideoIndex === index ? 'pause' : 'play'}-button`}
                    >
                      {isVideoPlaying && currentVideoIndex === index ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <rect x="6" y="4" width="4" height="16" />
                          <rect x="14" y="4" width="4" height="16" />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      )}
                    </button>
                    <div className="flex-grow min-w-0 flex flex-col">
                      <div 
                        className={`w-32 h-20 mb-2 bg-gray-100 rounded overflow-hidden cursor-pointer relative ${previewingIndex === index ? 'ring-2 ring-blue-500' : ''} [&_.ytp-chrome-top]:!hidden [&_.ytp-show-cards-title]:!hidden [&_.ytp-gradient-top]:!hidden [&_.ytp-gradient-bottom]:!hidden [&_.ytp-chrome-bottom]:!hidden [&_.ytp-watermark]:!hidden`}
                        style={{
                          overflow: 'hidden'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewingIndex(previewingIndex === index ? null : index);
                        }}
                        title={previewingIndex === index ? 'Click to stop preview' : 'Click to preview'}
                      >
                        <div 
                          style={{ 
                            pointerEvents: 'none',
                            position: 'relative',
                            width: '100%',
                            height: '100%'
                          }}
                        >
                          <ReactPlayer
                            url={video.url}
                            width="100%"
                            height="100%"
                            playing={previewingIndex === index}
                            muted={true}
                            controls={false}
                            light={true}
                            config={{
                              youtube: {
                                playerVars: {
                                  modestbranding: 1,
                                  controls: 0,
                                  showinfo: 0,
                                  rel: 0,
                                  disablekb: 1,
                                  playsinline: 1,
                                  enablejsapi: 1,
                                  fs: 0,
                                  iv_load_policy: 3,
                                  origin: window.location.origin,
                                  widget_referrer: window.location.origin,
                                  color: 'white',
                                  autohide: 1,
                                  cc_load_policy: 0,
                                  annotations: 0,
                                  branding: 0,
                                  end_screen: 0,
                                  info: 0,
                                  keyboard: 0,
                                  logo: 0,
                                  related: 0,
                                  title: 0,
                                  watermark: 0
                                }
                              }
                            }}
                          />
                        </div>
                      </div>
                      <h3 className="font-medium truncate">
                        Video {index + 1}
                        {video.loop && (
                          <span className="ml-2 text-blue-600 text-sm">
                            🔄 Loop
                          </span>
                        )}
                      </h3>
                    </div>
                  </div>
                )
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              No videos configured. Add videos in the Entertainment Settings.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreGameOverlay;
